import { useMemo } from "react";
import { Users, AlertCircle, CheckCircle2, UserX, Flag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar } from "@/components/shared";

const PRIORITY_COLORS = { high: "text-orange-500", medium: "text-blue-500", low: "text-slate-400" };

const isOverdue = (task) => task.due_date && !task.completed && new Date(task.due_date) < new Date(new Date().toDateString());

const MemberRow = ({ member, memberTasks, onEditTask }) => {
  const done = memberTasks.filter(t => t.completed).length;
  const overdue = memberTasks.filter(isOverdue).length;
  const pct = memberTasks.length ? Math.round((done / memberTasks.length) * 100) : 0;

  return (
    <div className="bg-card border rounded-xl p-4" data-testid={`workload-member-${member.id}`}>
      <div className="flex items-center gap-3 mb-3">
        {member.picture ? (
          <img src={member.picture} alt="" className="w-9 h-9 rounded-full" />
        ) : (
          <Avatar name={member.name || member.email || 'User'} size="md" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{member.name}</p>
          {member.email && <p className="text-xs text-muted-foreground truncate">{member.email}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {overdue > 0 && <Badge variant="outline" className="text-xs bg-red-500/10 text-red-600 border-red-200"><AlertCircle className="h-3 w-3 mr-1" />{overdue} overdue</Badge>}
          <Badge variant="secondary" className="text-xs">{memberTasks.length} task{memberTasks.length !== 1 ? 's' : ''}</Badge>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-muted-foreground shrink-0">{done}/{memberTasks.length} done</span>
      </div>

      {memberTasks.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-3">No tasks assigned</p>
      ) : (
        <div className="space-y-1.5">
          {memberTasks.map(task => (
            <button key={task.id} onClick={() => onEditTask(task)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-muted/60 transition-colors text-left"
              data-testid={`workload-task-${task.id}`}>
              {task.completed ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <Flag className={`h-3.5 w-3.5 shrink-0 ${PRIORITY_COLORS[task.priority] || 'text-slate-400'}`} />}
              <span className={`text-sm truncate flex-1 ${task.completed ? 'line-through text-muted-foreground' : ''}`}>{task.title}</span>
              {task.due_date && (
                <span className={`text-xs shrink-0 ${isOverdue(task) ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                  {new Date(task.due_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const WorkloadView = ({ tasks, members, user, onEditTask }) => {
  const activeTasks = useMemo(() => tasks.filter(t => !t.archived), [tasks]);

  const { grouped, unassigned } = useMemo(() => {
    const grouped = members.map(m => ({ member: m, tasks: activeTasks.filter(t => t.assignee_ids?.includes(m.id)) }));
    grouped.sort((a, b) => b.tasks.length - a.tasks.length);
    const unassigned = activeTasks.filter(t => !t.assignee_ids || t.assignee_ids.length === 0);
    return { grouped, unassigned };
  }, [activeTasks, members]);

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-1 flex items-center gap-2" data-testid="workload-title">
          <Users className="h-5 w-5 text-primary" />Workload
        </h2>
        <p className="text-sm text-muted-foreground">{activeTasks.length} tasks across {members.length} member{members.length !== 1 ? 's' : ''}</p>
      </div>

      {members.length === 0 && unassigned.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No team members yet. Invite people to your team to see their workload.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ member, tasks: memberTasks }) => (
            <MemberRow key={member.id} member={member} memberTasks={memberTasks} onEditTask={onEditTask} />
          ))}

          {unassigned.length > 0 && (
            <MemberRow
              member={{ id: "unassigned", name: "Unassigned", email: "" }}
              memberTasks={unassigned}
              onEditTask={onEditTask}
            />
          )}
        </div>
      )}
    </div>
  );
};
